import { FormLayoutItemSchema } from 'features/libra/sections/formLayout'
import { SectionSchema } from 'features/libra/sections/sectionTypes'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { RolodexEntryQueryBody, useRolodexEntries } from 'features/rolodex'
import get from 'lodash/get'
import { useMemo } from 'react'
import { GeckoEntry, RolodexEntryType } from 'types'
import { z } from 'zod'

export type ConditionalFormConfig = z.infer<typeof ConditionalFormConfigSchema>

const ValueLookupConfigSchema = z.object({
  // Tags to take from the ticket to use as query tags to find lookup entries
  lookupTags: z.array(z.string()).default([]),
  // Type of the lookup entries to find
  lookupType: z.string(),
  // Path in the lookupNameType to retrieve a label from - used as the title in the value list
  lookupNamePath: z.string().optional(),
  // Type of the entry to use as the name for the lookup entries
  lookupNameType: z.string().optional(),
  // Record of form field to list of source field paths to retrieve the values from
  fields: z.record(z.string(), z.union([z.string(), z.array(z.string())])),
})

export type ValueLookupConfig = z.infer<typeof ValueLookupConfigSchema>

export type ValueLookupValueType = {
  sourceName: string
  sourceId: string
  nameEntry: GeckoEntry
  field: string
  value:
    | string
    | number
    | boolean
    | ReadonlyArray<string>
    | ReadonlyArray<number>
    | ReadonlyArray<boolean>
}

export function createLookupValues(
  entries: ReadonlyArray<GeckoEntry>,
  lookupType: string,
  lookupNameType: RolodexEntryType | undefined,
  lookupNamePath: string | undefined,
  fields: ValueLookupConfig['fields']
): ReadonlyArray<ValueLookupValueType> {
  const nameRecs = entries.filter((entry) =>
    lookupNameType ? entry.type === lookupNameType : entry.type === lookupType
  )
  const nameRecsMap = new Map(nameRecs.map((entry) => [entry.id, entry]))
  const lookupRecs = entries.filter((entry) => entry.type === lookupType)

  return lookupRecs.flatMap((entry) => {
    const nameRecId =
      lookupNameType && lookupNameType !== lookupType
        ? entry.tags[lookupNameType as RolodexEntryType]
        : entry.id
    const nameRec = nameRecsMap.get(nameRecId ?? entry.id)
    const sourceName =
      nameRec && lookupNamePath
        ? (get(nameRec.data, lookupNamePath) ?? entry.id)
        : entry.id

    return Object.entries(fields)
      .flatMap(([formField, paramObjectField]) => {
        const normObjectField = Array.isArray(paramObjectField)
          ? paramObjectField
          : [paramObjectField]

        return normObjectField.map((objectField) => {
          const objectFieldValue = get(entry.data ?? {}, objectField)
          return objectFieldValue !== undefined && objectFieldValue !== null
            ? {
                sourceName,
                sourceId: entry.id,
                field: formField,
                value: objectFieldValue as ValueLookupValueType['value'],
                nameEntry: nameRec ?? entry,
              }
            : undefined
        })
      })
      .filter((value): value is ValueLookupValueType => value !== undefined)
  })
}

function isTagsEmpty(tags: RolodexEntryQueryBody['tags']): boolean {
  const tagsEntries = Object.entries(tags ?? {})
  return (
    tagsEntries.length === 0 ||
    tagsEntries.every(
      ([, values]) =>
        values === undefined || (Array.isArray(values) && values.length === 0)
    )
  )
}

export function useValueLookups(config: ValueLookupConfig | undefined): {
  values: ReadonlyArray<ValueLookupValueType>
  isLoading: boolean
} {
  const { lookupTags, lookupType, lookupNameType, lookupNamePath, fields } =
    config ?? {
      lookupTags: [],
      lookupType: '',
      lookupNameType: undefined,
      lookupNamePath: undefined,
      fields: {},
    }
  const { ticketTags } = useTicketAccess()
  const queryTags = useMemo(() => {
    return lookupTags.reduce(
      (acc, tagType) => {
        const ticketTagVal = ticketTags[tagType as RolodexEntryType]
        if (ticketTagVal) {
          return { ...acc, [tagType]: ticketTagVal }
        }
        return acc
      },
      {} as Record<RolodexEntryType, string>
    )
  }, [lookupTags, ticketTags])
  const links = useMemo(
    () =>
      lookupNameType && lookupNameType !== lookupType
        ? [lookupNameType as RolodexEntryType]
        : undefined,
    [lookupNameType, lookupType]
  )
  const { entries, isLoading } = useRolodexEntries(
    useMemo(
      () =>
        !isTagsEmpty(queryTags)
          ? {
              type: lookupType as RolodexEntryType,
              links,
              tags: queryTags,
            }
          : undefined,
      [lookupType, queryTags, links]
    )
  )

  const values = useMemo<ReadonlyArray<ValueLookupValueType>>(
    () =>
      createLookupValues(
        entries,
        lookupType,
        lookupNameType as RolodexEntryType | undefined,
        lookupNamePath,
        fields
      ),
    [entries, fields, lookupNamePath, lookupNameType, lookupType]
  )
  return useMemo(
    () => ({
      values,
      isLoading,
    }),
    [values, isLoading]
  )
}

export const ConditionalFormConfigSchema = z.object({
  form: z.array(FormLayoutItemSchema),
  sections: z.array(SectionSchema),
  lookups: ValueLookupConfigSchema.optional(),
})
